import { useTranslation } from "react-i18next";
import {
  ContactAgreed,
  Contribution,
  CustomerRelationship,
  DealEventType,
  DealReadiness,
  NewsCategory,
  NewsStatus,
  OpportunityType,
  RegistrationStatus,
  SalesStatus,
  ServiceArea,
} from "../../../Models/ambassador";

/** The numeric values of a TypeScript enum, in declaration order. */
export const enumValues = <T extends number>(
  values: Record<string, string | T>,
) =>
  Object.values(values).filter(
    (value): value is T => typeof value === "number",
  );

export const REGISTRATION_STATUSES =
  enumValues<RegistrationStatus>(RegistrationStatus);
export const SALES_STATUSES = enumValues<SalesStatus>(SalesStatus);
export const CUSTOMER_RELATIONSHIPS =
  enumValues<CustomerRelationship>(CustomerRelationship);
export const OPPORTUNITY_TYPES = enumValues<OpportunityType>(OpportunityType);
export const SERVICE_AREAS = enumValues<ServiceArea>(ServiceArea);
export const DEAL_READINESS = enumValues<DealReadiness>(DealReadiness);
export const CONTRIBUTIONS = enumValues<Contribution>(Contribution);
export const CONTACT_AGREED = enumValues<ContactAgreed>(ContactAgreed);
export const NEWS_CATEGORIES = enumValues<NewsCategory>(NewsCategory);
export const NEWS_STATUSES = enumValues<NewsStatus>(NewsStatus);

// The ambassador sees the sales statuses without "still open" and
// "negotiation" (shown as "offer submitted").
export const EXTERNAL_SALES_STATUSES = [
  SalesStatus.QUALIFICATION,
  SalesStatus.OFFER_IN_PREPARATION,
  SalesStatus.OFFER_SUBMITTED,
  SalesStatus.WON,
  SalesStatus.LOST,
];

export const COUNTRIES = [
  "DE",
  "AT",
  "CH",
  "LI",
  "LU",
  "NL",
  "BE",
  "FR",
  "IT",
  "ES",
  "PL",
  "CZ",
  "DK",
  "SE",
  "NO",
  "FI",
  "GB",
  "IE",
  "US",
];

/** Translated labels of the Ambassador Workspace values. */
export const useAmbassadorLabels = () => {
  const { t, i18n } = useTranslation("ambassador");

  const countryNames = (() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      return null;
    }
  })();

  return {
    registrationStatus: (value: RegistrationStatus) =>
      t(`status.registration.${value}`),
    salesStatus: (value: SalesStatus) => t(`status.sales.${value}`),
    customerRelationship: (value: CustomerRelationship) =>
      t(`options.customerRelationship.${value}`),
    opportunityType: (value: OpportunityType) =>
      t(`options.opportunityType.${value}`),
    serviceArea: (value: ServiceArea) => t(`options.serviceArea.${value}`),
    dealReadiness: (value: DealReadiness) =>
      t(`options.dealReadiness.${value}`),
    contribution: (value: Contribution) => t(`options.contribution.${value}`),
    contactAgreed: (value: ContactAgreed) =>
      t(`options.contactAgreed.${value}`),
    newsCategory: (value: NewsCategory) => t(`options.newsCategory.${value}`),
    newsStatus: (value: NewsStatus) => t(`options.newsStatus.${value}`),
    event: (value: DealEventType) => t(`history.${value}`),
    country: (code: string) => countryNames?.of(code) ?? code,
  };
};

export type AmbassadorLabels = ReturnType<typeof useAmbassadorLabels>;
